import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { organisationSecretApi } from "../../api/organizationSecretApi";
import type { OrganisationSecret } from "../../types/organisationSecret.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";

interface OrganisationSecretState {
  items: OrganisationSecret[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: OrganisationSecretState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const organisationSecretSlice = createSlice({
  name: "organisationSecret",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setOrganisationSecrets: (
      state,
      action: PayloadAction<OrganisationSecret[]>,
    ) => {
      state.items = action.payload;
    },
    appendOrganisationSecrets: (
      state,
      action: PayloadAction<OrganisationSecret[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleOrganisationSecretLocally: (
      state,
      action: PayloadAction<OrganisationSecret>,
    ) => {
      state.items = [action.payload, ...state.items];
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetOrganisationSecretList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleOrganisationSecret: (
      state,
      action: PayloadAction<Partial<OrganisationSecret> & { id: string }>,
    ) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleOrganisationSecret: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setOrganisationSecrets,
  appendOrganisationSecrets,
  addSingleOrganisationSecretLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetOrganisationSecretList,
  updateSingleOrganisationSecret,
  removeSingleOrganisationSecret,
} = organisationSecretSlice.actions;

export const fetchOrganisationSecrets = (
  payload: {
    pageSize?: number;
    pageIndex?: number;
    orderBy?: string;
    sortDecending?: boolean;
    secretDefinitionId?: string;
    status?: string;
  } = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().organisationSecret;
    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex!
      : state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "",
        sortDecending: payload.sortDecending ?? true,
        ...(payload.secretDefinitionId && {
          secretDefinitionId: payload.secretDefinitionId,
        }),
        ...(payload.status && { status: payload.status }),
      };

      const response =
        await organisationSecretApi.searchOrganisationSecrets(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count ?? response.totalCount ?? 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      dispatch(
        setHasMore(
          currentLoadedCount < totalCount &&
            fetchedData.length >= (payload.pageSize || 10),
        ),
      );

      if (incomingPageIndex === 0) {
        dispatch(setOrganisationSecrets(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendOrganisationSecrets(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Organisation Secrets error:", error);
      toast.error(
        i18n.t("common:slices.organisationSecrets.fetchError", {
          defaultValue: "Failed to fetch organisation secrets",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addOrganisationSecret = (data: Partial<OrganisationSecret>) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const res = await organisationSecretApi.createOrganisationSecret(data);
      const createdItem = res?.data || res;

      if (createdItem && createdItem.id) {
        dispatch(addSingleOrganisationSecretLocally(createdItem));
      } else {
        dispatch(fetchOrganisationSecrets({ pageIndex: 0 }));
      }

      toast.success(
        i18n.t("common:slices.organisationSecrets.addSuccess", {
          defaultValue: "Organisation secret created successfully",
        }),
      );
      return createdItem;
    } catch (error) {
      console.error("Create Organisation Secret error:", error);
      toast.error(
        i18n.t("common:slices.organisationSecrets.addError", {
          defaultValue: "Failed to create organisation secret",
        }),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editOrganisationSecret = (
  data: Partial<OrganisationSecret> & { id: string },
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));
      const res = await organisationSecretApi.updateOrganisationSecret(data);
      const updatedItem = res?.data || data;

      dispatch(updateSingleOrganisationSecret(updatedItem));

      toast.success(
        i18n.t("common:slices.organisationSecrets.editSuccess", {
          defaultValue: "Organisation secret updated successfully",
        }),
      );
      return updatedItem;
    } catch (error) {
      console.error("Edit Organisation Secret error:", error);
      toast.error(
        i18n.t("common:slices.organisationSecrets.editError", {
          defaultValue: "Failed to update organisation secret",
        }),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeOrganisationSecret = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleOrganisationSecret(id));

      await organisationSecretApi.removeOrganisationSecret(id);

      toast.success(
        i18n.t("common:slices.organisationSecrets.removeSuccess", {
          defaultValue: "Organisation secret removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Organisation Secret error:", error);
      toast.error(
        i18n.t("common:slices.organisationSecrets.removeError", {
          defaultValue: "Failed to remove organisation secret",
        }),
      );
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default organisationSecretSlice.reducer;
